import type { KkiRateLike } from "@karama/shared";

interface SnapshotRate {
	month: string;
	kk_value: number;
	methodology_version: string;
	basket_version: string | null;
	alpha: number | null;
}

interface SnapshotData {
	generated_at: string;
	methodology_version: string;
	countries: Record<string, SnapshotRate[]>;
}

export interface BundledKkiRate extends KkiRateLike {
	countryCode: string;
	month: string;
	methodologyVersion: string;
	freshness: "bundled";
}

let snapshotCache: SnapshotData | null = null;

function currentMonthFirst(): string {
	const d = new Date();
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	return `${y}-${m}-01`;
}

async function loadSnapshot(): Promise<SnapshotData | null> {
	if (snapshotCache) return snapshotCache;
	try {
		const res = await fetch("/kki-snapshot.json");
		if (!res.ok) return null;
		snapshotCache = (await res.json()) as SnapshotData;
		return snapshotCache;
	} catch {
		return null;
	}
}

/** Resolve bundled KKI rate (anon-safe, no auth). */
export async function resolveBundledKkiRate(
	countryCode: string,
	month?: string,
): Promise<BundledKkiRate | null> {
	const snapshot = await loadSnapshot();
	if (!snapshot) return null;

	const countryRates = snapshot.countries[countryCode.toUpperCase()];
	if (!countryRates?.length) return null;

	const sorted = [...countryRates].sort((a, b) => a.month.localeCompare(b.month));
	const targetMonth = month ?? currentMonthFirst();

	const exact = sorted.find((r) => r.month === targetMonth);
	let rate: SnapshotRate;

	if (exact) {
		rate = exact;
	} else if (targetMonth < sorted[0].month) {
		rate = sorted[0];
	} else if (targetMonth > sorted[sorted.length - 1].month) {
		rate = sorted[sorted.length - 1];
	} else {
		let nearest = sorted[0];
		for (const entry of sorted) {
			if (entry.month <= targetMonth) nearest = entry;
			else break;
		}
		rate = nearest;
	}

	if (!rate || rate.kk_value <= 0) return null;

	return {
		countryCode: countryCode.toUpperCase(),
		month: rate.month,
		methodologyVersion:
			rate.methodology_version ?? snapshot.methodology_version,
		kkValue: rate.kk_value,
		freshness: "bundled",
	};
}

export function formatBundledMonthLabel(monthIso: string): string {
	const d = new Date(monthIso);
	const months = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];
	return `${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
